// src/components/invoices/InvoiceForm.jsx
import React, { useState, useEffect, useRef } from 'react';
import { X, Upload, Download, Check, Trash, FileText, Clipboard, CreditCard, Calendar } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import useAuth from '../../hooks/useAuth';
import invoiceService from '../../services/invoiceService';
import ExchangeRateWidget from '../public/ExchangeRateWidget';

const InvoiceForm = ({ isOpen, onClose, onSubmit, invoice, title, isViewMode = false, isLoading = false, showNotification }) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const GeltoAmdRef = useRef(null);
  
  const serviceTypePrices = {
    'transitCard': 355,
    'truckInsurance': 610,
    'trailerInsurance': 145,
    'parking_40': 40,
    'parking_80': 80
  };

  const insurancePrices = {
    'truckInsurance': {
      '15 Days': 60,
      '30 Days': 100,
      '90 Days': 170,
      '1 Year': 610
    },
    'trailerInsurance': {
      '15 Days': 14,
      '30 Days': 25,
      '90 Days': 40,
      '1 Year': 145
    }
  };
  
  const initialFormData = {
    company: user?.username || '',
    service_type: '',
    quantity: '',
    price: '',
    commission: user?.commission || 0,
    total_amountGel: '',
    total_amount: '',
    payment_type: '',
    paid: false,
    status: 'pending',
    file: null,
    insurance_period: ''
  };
  
  const [formData, setFormData] = useState(initialFormData);
  const [previewFile, setPreviewFile] = useState(null);
  const [fileToDelete, setFileToDelete] = useState(false);
  const [errors, setErrors] = useState({});
  const [isPriceEditable, setIsPriceEditable] = useState(true);
  const [fileChanged, setFileChanged] = useState(false);
  const [skipAutoCalc, setSkipAutoCalc] = useState(false);
  
  useEffect(() => {
    if (!isOpen) return;

    setSkipAutoCalc(false);

    const fetchInvoiceData = async () => {
      if (invoice?.id) {
        try {
          const data = await invoiceService.getById(invoice.id);
          if (data) {
            const processedInvoice = {
              ...data,
              quantity: data.quantity?.toString() || '',
              price: data.price?.toString() || '',
              total_amountGel: data.total_amountGel?.toString() || '',
              total_amount: data.total_amount?.toString() || '',
              insurance_period: data.insurance_period || '',
              exchange_rate: data.exchange_rate || ''
            };
            setFormData(processedInvoice);
            setSkipAutoCalc(true);
            
            if (data.file) {
              setPreviewFile({
                name: data.file.split('/').pop(),
                url: data.file
              });
            }
            
            setIsPriceEditable(!serviceTypePrices.hasOwnProperty(processedInvoice.service_type));
          }
        } catch (error) {
          console.error('Error fetching invoice:', error);
        }
      } else {
        setFormData(initialFormData);
        setPreviewFile(null);
        setIsPriceEditable(true);
      }
      
      setFileToDelete(false);
      setFileChanged(false);
      setErrors({});
    };
    
    fetchInvoiceData();
  }, [isOpen, invoice?.id, user]);
  
  useEffect(() => {
    if (skipAutoCalc) {
      setSkipAutoCalc(false);
      return;
    }
    
    if (formData.quantity && formData.price) {
      const quantity = parseFloat(formData.quantity) || 0;
      const price = parseFloat(formData.price) || 0;
      const commission = parseFloat(formData.commission) || 0;
      const subtotal = quantity * price;
      const commissionAmount = subtotal * (commission / 100);
      const totalAmountGEL = subtotal + commissionAmount;
      const sellRate = GeltoAmdRef.current?.getSellRate() || formData.exchange_rate || 1; 
      const totalAmount = sellRate ? totalAmountGEL * sellRate*1.2 : null;
      
      setFormData(prev => ({ 
        ...prev, 
        total_amountGel: totalAmountGEL,
        total_amount: totalAmount,
        exchange_rate: sellRate 
      }));
    }
  }, [formData.quantity, formData.price, formData.commission, skipAutoCalc]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));

      if (name === 'service_type') {
        const fixedPrice = serviceTypePrices[value];
        if (fixedPrice) {
          setFormData(prev => ({ ...prev, price: fixedPrice.toString() }));
          setIsPriceEditable(false);
        } else {
          setIsPriceEditable(true);
        }
        // Reset insurance_period when service type changes
        setFormData(prev => ({ ...prev, insurance_period: '' }));
      }

      if (name === 'insurance_period') {
        const periodPrice = insurancePrices[formData.service_type]?.[value];
        if (periodPrice) {
          setFormData(prev => ({ ...prev, price: periodPrice.toString() }));
        }
      }
    }

    setErrors(prev => ({ ...prev, [name]: '' }));
  };
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const maxSize = 5 * 1024 * 1024;
    
    if (!allowedTypes.includes(file.type)) {
      setErrors(prev => ({ ...prev, file: t('invalidFileType') }));
      return;
    }
    
    if (file.size > maxSize) {
      setErrors(prev => ({ ...prev, file: t('fileTooLarge') }));
      return;
    }
    
    setFormData(prev => ({ ...prev, file }));
    setPreviewFile({ name: file.name });
    setFileToDelete(false);
    setErrors(prev => ({ ...prev, file: '' }));
    setFileChanged(true);
  };
  
  const handleFileDrop = (e) => {
    e.preventDefault();
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      const maxSize = 5 * 1024 * 1024;
      
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, file: t('invalidFileType') }));
        return;
      }
      
      if (file.size > maxSize) {
        setErrors(prev => ({ ...prev, file: t('fileTooLarge') }));
        return;
      }
      
      setFormData(prev => ({ ...prev, file }));
      setPreviewFile({ name: file.name });
      setFileToDelete(false);
      setErrors(prev => ({ ...prev, file: '' }));
      setFileChanged(true);
    }
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
  };
  
  const handleDeleteFile = () => {
    setFormData(prev => ({ ...prev, file: null }));
    setPreviewFile(null);
    setFileToDelete(true);
    setFileChanged(false);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const validate = () => {
    const newErrors = {};
    
    if (!formData.company) newErrors.company = t('required');
    if (!formData.service_type) newErrors.service_type = t('required');
    if (!formData.quantity) newErrors.quantity = t('required');
    if (!formData.price) newErrors.price = t('required');
    if (!formData.payment_type) newErrors.payment_type = t('required');
    
    // Validate insurance_period for vehicle and trailer insurance
    if ((formData.service_type === 'truckInsurance' || formData.service_type === 'trailerInsurance') && !formData.insurance_period) {
      newErrors.insurance_period = t('required');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    const submitData = new FormData();
    submitData.append('company', formData.company);
    submitData.append('service_type', formData.service_type);
    submitData.append('quantity', formData.quantity);
    submitData.append('price', formData.price);
    submitData.append('commission', formData.commission);
    submitData.append('total_amountGel', formData.total_amountGel);
    submitData.append('total_amount', formData.total_amount);
    submitData.append('payment_type', formData.payment_type);
    submitData.append('paid', formData.paid);
    submitData.append('status', formData.status);
	
	const currentSellRate = GeltoAmdRef.current?.getSellRate() || 1;
    submitData.append('exchange_rate', currentSellRate);
	
    if (formData.insurance_period) {
      submitData.append('insurance_period', formData.insurance_period);
    }
    
    if (formData.file instanceof File) {
      submitData.append('file', formData.file);
    } else if (fileToDelete) {
      submitData.append('delete_file', 'true');
    }
    
    if (invoice?.id) {
      submitData.append('id', invoice.id);
    }
    
    onSubmit(submitData);
  };
  
  if (!isOpen) return null;
  
  const showInsurancePeriod = formData.service_type === 'truckInsurance' || formData.service_type === 'trailerInsurance';
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center overflow-y-auto p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-5xl max-h-[90vh] flex flex-col relative">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 z-10"
        >
          <X className="h-6 w-6" />
        </button>
        
        {/* Header */}
         <div className="flex justify-between items-center p-5 border-b border-gray-200 bg-gradient-to-r from-slate-800 to-slate-700 text-white">
          <h2 className="text-xl font-semibold flex items-center">{title}</h2>

        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto flex">
          <form onSubmit={handleSubmit} className="flex-1 flex">
            {/* Left Column: Form Fields */}
            <div className="flex-1 p-6">
              {/* Company Field */}

              
              {/* Service Type and Quantity Row */}
              <div className="grid grid-cols-2 gap-6 mb-6">
                {/* Service Type */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('serviceType')} <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="service_type"
                    value={formData.service_type}
                    onChange={handleChange}
                    disabled={isViewMode}
                    className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                      isViewMode ? 'bg-gray-50 text-gray-700' : 'bg-white'
                    } ${errors.service_type ? 'border-red-500' : 'border-gray-300'}`}
                  >
                    <option value="">{t('select')}</option>
                    <option value="transitCard">{t('transitCard')}</option>
                    <option value="truckInsurance">{t('truckInsurance')}</option>
                    <option value="trailerInsurance">{t('trailerInsurance')}</option>
                    <option value="parking_40">{t('parking_40')}</option>
                    <option value="parking_80">{t('parking_80')}</option>
					<option value="other">{t('other')}</option>
                  </select>
                  {errors.service_type && <p className="mt-1 text-sm text-red-500">{errors.service_type}</p>}
                </div>
                
                {/* Quantity */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('quantity')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleChange}
                    disabled={isViewMode}
                    className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                      isViewMode ? 'bg-gray-50 text-gray-700' : 'bg-white'
                    } ${errors.quantity ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="0"
                  />
                  {errors.quantity && <p className="mt-1 text-sm text-red-500">{errors.quantity}</p>}
                </div>
              </div>
              
              {/* Insurance Period (conditional) */}
              {showInsurancePeriod && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    {t('insurancePeriod')} <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {['15 Days', '30 Days', '90 Days', '1 Year'].map((period) => (
                      <label key={period} className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="insurance_period"
                          value={period}
                          checked={formData.insurance_period === period}
                          onChange={handleChange}
                          disabled={isViewMode}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">{period}</span>
                      </label>
                    ))}
                  </div>
                  {errors.insurance_period && <p className="mt-2 text-sm text-red-500">{errors.insurance_period}</p>}
                </div>
              )}
              
              {/* Price and Commission Row */}
              <div className="grid grid-cols-2 gap-6 mb-6">
                {/* Price */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('price')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    disabled={isViewMode || !isPriceEditable}
                    className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                      isViewMode || !isPriceEditable ? 'bg-gray-50 text-gray-700' : 'bg-white'
                    } ${errors.price ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="0"
                  />
                  {errors.price && <p className="mt-1 text-sm text-red-500">{errors.price}</p>}
                  {!isPriceEditable && <p className="mt-1 text-xs text-gray-500">{t('fixedPrice')}</p>}
                </div>
                
                {/* Commission */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('commission')} (%)
                  </label>
                  <input
                    type="number"
                    name="commission"
                    value={formData.commission}
                    onChange={handleChange}
                    disabled
                    className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors 'bg-gray-50 text-gray-700' : 'bg-white'`}
                    placeholder="0"
                  />
                </div>
              </div>
              
              {/* Payment Type */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('paymentType')} <span className="text-red-500">*</span>
                </label>
                <select
                  name="payment_type"
                  value={formData.payment_type}
                  onChange={handleChange}
                  disabled={isViewMode}
                  className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                    isViewMode ? 'bg-gray-50 text-gray-700' : 'bg-white'
                  } ${errors.payment_type ? 'border-red-500' : 'border-gray-300'}`}
                >
                  <option value="">{t('selectPaymentType')}</option>
                  <option value="cash">{t('cash')}</option>
                  <option value="bankTransfer">{t('bankTransfer')}</option>
                </select>
                {errors.payment_type && <p className="mt-1 text-sm text-red-500">{errors.payment_type}</p>}
              </div>
              
              {/* Totals Section */}
              <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-md border border-blue-200">
                <div className={`grid ${formData.tts_georgia ? 'grid-cols-1' : 'grid-cols-3'} gap-4`}>
                  <div>
                    <p className="text-xs text-gray-600 mb-1">{t('totalAmountGEL')}</p>
                    <p className="text-lg font-semibold text-blue-600">
                      ₾ {parseFloat(formData.total_amountGel || 0).toFixed(2)}
                    </p>
                  </div>
                  {!formData.tts_georgia && (
                    <>
                      <div>
                        <p className="text-xs text-gray-600 mb-1">{t('exchangeRate')}</p>
                        <p className="text-lg font-semibold text-gray-700">
                          {parseFloat(formData.exchange_rate || 1).toFixed(4)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 mb-1">{t('totalAmount')}</p>
                        <p className="text-lg font-semibold text-green-600">
                          ֏ {parseFloat(formData.total_amount || 0).toFixed(2)}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>
              
              {/* Status and Paid Checkbox */}
              <div className="mb-6 flex items-center space-x-6">
                <div className="flex-1">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('status')}
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    disabled
                    className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors'bg-gray-50 text-gray-700' : 'bg-white'`}
                  >
                    <option value="pending">{t('pending')}</option>
                    <option value="completed">{t('completed')}</option>
                    <option value="cancelled">{t('cancelled')}</option>
                  </select>
                </div>
                
                <label className="flex items-center space-x-2 cursor-pointer mt-6">
                  <input
                    type="checkbox"
                    name="paid"
                    checked={formData.paid}
                    onChange={handleChange}
                    disabled
                    className="h-4 w-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 border-gray-300"
                  />
                  <span className="text-sm font-semibold text-gray-700">{t('markAsPaid')}</span>
                </label>
              </div>
              
              {/* Exchange Rate Widget — only when editing/creating, not in view mode */}
              {!isViewMode && (
                <div className="mb-6">
                  <ExchangeRateWidget ref={GeltoAmdRef} />
                </div>
              )}
              
              {/* File Upload */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-4">
                  {t('attachFile')}
                </label>
                
                {previewFile && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-md">
                      <div className="flex items-center space-x-2">
                        <FileText className="h-5 w-5 text-green-600" />
                        <span className="text-sm font-medium text-gray-700">{previewFile.name}</span>
                      </div>
                      {!isViewMode && (
                        <button
                          type="button"
                          onClick={handleDeleteFile}
                          className="text-red-600 hover:text-red-800 focus:outline-none"
                        >
                          <Trash className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </div>
                )}
                
                {/* File upload area */}
                {!isViewMode && (
                  <div 
                    className={`border-2 border-dashed rounded-md p-6 flex flex-col items-center justify-center ${errors.file ? 'border-red-500' : 'border-gray-300'} hover:border-blue-500 cursor-pointer transition-colors duration-200`}
                    onDrop={handleFileDrop}
                    onDragOver={handleDragOver}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="h-10 w-10 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-500 mb-1">{t('dragAndDropFile')}</p>
                    <p className="text-xs text-gray-400">{t('or')}</p>
                    <button
                      type="button"
                      className="mt-2 px-4 py-2 text-sm text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                    >
                      {t('browseFiles')}
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      className="hidden"
                      onChange={handleFileChange}
                    />
                  </div>
                )}
                
                {errors.file && <p className="mt-1 text-sm text-red-500">{errors.file}</p>}
              </div>
            </div>
            
            {/* Right Column: Transactions */}
            <div className="flex-1 p-6 bg-gray-50 border-l border-gray-200 overflow-y-auto">
              {invoice?.id ? (
                <>
                  <div className="mb-6">
                    <h3 className="text-lg font-medium text-gray-800 flex items-center mb-4">
                      <span className="mr-2">💵</span> {t('transactions')}
                    </h3>
                    
                    {/* Paid/Unpaid Summary */}
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="bg-white p-4 rounded-md shadow border border-gray-200">
                        <p className="text-sm text-gray-500">{t('paid')}</p>
                        <p className="text-xl font-semibold text-green-600">
                          {new Intl.NumberFormat('en-US', { style: 'currency', currency: invoice.tts_georgia ? 'GEL' : 'AMD' }).format(invoice.paid_amount || 0)}
                        </p>
                      </div>
                      <div className="bg-white p-4 rounded-md shadow border border-gray-200">
                        <p className="text-sm text-gray-500">{t('unpaid')}</p>
                        <p className="text-xl font-semibold text-red-600">
                          {new Intl.NumberFormat('en-US', { style: 'currency', currency: invoice.tts_georgia ? 'GEL' : 'AMD' }).format(((invoice.tts_georgia ? invoice.total_amountGel : invoice.total_amount) || 0) - (invoice.paid_amount || 0))}
                        </p>
                      </div>
                    </div>
                    
                    {/* Transactions List */}
                    {invoice.transactions?.length > 0 ? (
                      <div className="space-y-3">
                        {invoice.transactions.map((transaction) => (
                          <div key={transaction.id} className="bg-white p-3 rounded-md shadow-sm border border-gray-200 hover:shadow transition-shadow">
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-sm font-medium">{new Date(transaction.created_at).toLocaleDateString()}</span>
                              <span className={`text-sm font-semibold ${transaction.type === 'payment' ? 'text-green-600' : 'text-red-600'}`}>
                                {transaction.type === 'payment' ? '+' : '-'} {new Intl.NumberFormat('en-US', { style: 'currency', currency: invoice.tts_georgia ? 'GEL' : 'AMD' }).format(transaction.amount)}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-gray-500">
                                {transaction.created_by_username || 'System'}
                              </span>
                              {transaction.file && (
                                <a 
                                  href={transaction.file} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800 flex items-center text-xs"
                                >
                                  <Download className="h-3 w-3 mr-1" />
                                  {t('downloadReceipt')}
                                </a>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-10 bg-white rounded-md border border-gray-200 shadow-sm">
                        <FileText className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                        <p className="text-gray-500">{t('noTransactions')}</p>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-gray-500">
                  <FileText className="h-12 w-12 mb-4 text-gray-400" />
                  <p className="text-center">{t('transactionsWillAppearHere')}</p>
                  <p className="text-center text-sm text-gray-400 mt-2">
                    {t('transactionsDescription')}
                  </p>
                </div>
              )}
            </div>
          </form>
        </div>
        
        {/* Footer with buttons */}
        <div className="p-4 border-t bg-gray-50 flex justify-end space-x-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            {isViewMode ? t('close') : t('cancel')}
          </button>
          
          {/* Update File button (visible only in view mode when file is changed) */}
          {isViewMode && fileChanged && (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('updating')}
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  {t('updateFile')}
                </>
              )}
            </button>
          )}
          
          {/* Regular Save/Update button (visible when not in view mode) */}
          {!isViewMode && (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('saving')}
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  {invoice?.id ? t('update') : t('save')}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoiceForm;